import { Component, Input, OnInit, ViewChildren, SimpleChanges } from '@angular/core';
import { GraphUser } from '../../models/graph-user.model';
import _ from 'lodash';
import { FormControl } from '@angular/forms';
import { ErrorDialogComponent } from '../../error-dialog/error-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DataService } from '../../services/data.service';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { DOA_PROJFUND_EMAILS, DOA_RE_BUILDING_EMAILS } from '../common/real-estate-form-options';

@Component({
  selector: 'app-real-estate-project-approver',
  templateUrl: './real-estate-project-approver.component.html',
  styleUrls: ['./real-estate-project-approver.component.scss'],
  standalone: false,
})
export class RealEstateProjectApproverComponent implements OnInit {
  @Input() approverList: any[] = [];
  @Input() doaAmounts: number[] = [];
  @Input() reviewMode: boolean = false;

  additionalApprovers: GraphUser[] = [];
  xmlDoc: any;
  parser: DOMParser = new DOMParser();
  additionalApproverControl: FormControl[] = [];
  additionalApproverOptions: GraphUser[][] = [];

  @ViewChildren(MatAutocompleteTrigger) triggers;

  constructor(
    private dialog: MatDialog,
    private dataService: DataService
  ) {}

  ngOnInit() {
    this.initializeApprovers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle approverList changes
    if (changes['approverList']) {
      this.initializeApprovers();
    }

    // Handle reviewMode changes
    if (changes['reviewMode']) {
      if (this.reviewMode) {
        this.additionalApproverControl.forEach((control) => control.disable());
      } else {
        this.additionalApproverControl.forEach((control) => control.enable());
      }
    }
  }

  private initializeApprovers() {
    // Force approverList to be an array and handle various input types
    if (!this.approverList) {
      this.approverList = [];
    } else if (typeof this.approverList === 'object' && !Array.isArray(this.approverList)) {
      // If it's an object, convert it to an array or set to empty array
      this.approverList = Object.values(this.approverList).filter((item) => item != null) || [];
    } else if (!Array.isArray(this.approverList)) {
      this.approverList = [];
    }

    // Clear existing arrays to prevent duplication
    this.additionalApprovers = [];
    this.additionalApproverControl = [];
    this.additionalApproverOptions = [];

    // Initialize the arrays based on the approverList
    this.approverList.forEach((approver, index) => {
      this.additionalApprovers.push(approver);
      this.additionalApproverControl.push(new FormControl(approver));
      this.additionalApproverOptions.push([]);
    });
  }

  autocompleteName(index: number) {
    return 'additionalApprover' + index.toString();
  }

  addNewAdditionalApprover() {
    this.additionalApproverControl.push(new FormControl());
    this.additionalApprovers.push(null);
    this.additionalApproverOptions.push([]);
    this.approverList.push('');
    this.doaAmounts.push(0);
  }

  displayFnAdditionalApprover(value: GraphUser): any | undefined {
    if (!value) {
      return '';
    } else if (value.givenName && value.surname && value.jobTitle) {
      return value.givenName + ' ' + value.surname + ' (' + value.mail + '), ' + value.jobTitle;
    } else if (value.givenName && value.surname && !value.jobTitle) {
      return value.givenName + ' ' + value.surname + ' (' + value.mail + ')';
    } else if (value.displayName && value.jobTitle) {
      return value.displayName + ' (' + value.mail + '), ' + value.jobTitle;
    } else if (value.displayName && !value.jobTitle) {
      return value.displayName + ' (' + value.mail + ')';
    } else {
      return value;
    }
  }

  doaAmountMet() {
    for (let i = 0; i < this.doaAmounts.length; i++) {
      // TODO: Update this amount when we get the correct threshold
      if (this.doaAmounts[i] > 0) {
        return true;
      }
    }
    return false;
  }

  deleteApprover(index: number) {
    this.additionalApproverControl.splice(index, 1);
    this.additionalApprovers.splice(index, 1);
    this.additionalApproverOptions.splice(index, 1);
    this.approverList.splice(index, 1);
    this.doaAmounts.splice(index + 1, 1);
  }

  searchUsersAdditionalApprover(searchText: any, index: number) {
    this.dataService.searchUsers(searchText).subscribe((result) => {
      const users: GraphUser[] = result['value'];
      const filtered = users.filter(
        (user) =>
          !user.jobTitle.startsWith('CW-') ||
          (user.givenName === 'Jared' && user.surname === 'Kohler')
      );
      this.additionalApproverOptions[index] = _.orderBy(filtered, ['surname', 'givenName']);
      this.triggers._results[index].openPanel();
    });
  }

  additionalApproverSelected(event: any, index: number) {
    this.getDOA(event.mailNickname, index + 1, event.mail);
    this.approverList[index] = event;
  }

  deleteAdditionalApproverValue(i: number) {
    this.additionalApprovers[i] = '' as any;
  }

  getDOA(lanID: string, index: number, email?: string) {
    this.doaAmounts[index] = 0;
    let doc = document.implementation.createDocument('', '', null);
    const pi = doc.createProcessingInstruction('xml', 'version="1.0" encoding="UTF-8"');
    doc.insertBefore(pi, doc.firstChild);
    let elem1 = doc.createElement('soapenv:Envelope');
    elem1.setAttribute('xmlns:soapenv', 'http://schemas.xmlsoap.org/soap/envelope/');
    elem1.setAttribute('xmlns:web', 'http://duke-energy.com/webservices/');
    let elem2 = doc.createElement('soapenv:Header');
    let elem3 = doc.createElement('soapenv:Body');
    let elem4 = doc.createElement('web:GetDoaInformationByLanid');
    let elem5 = doc.createElement('web:lanids');
    let elem6 = doc.createElement('web:string');
    elem6.innerHTML = lanID;
    elem5.appendChild(elem6);
    elem4.appendChild(elem5);
    elem3.appendChild(elem4);
    elem1.appendChild(elem2);
    elem1.appendChild(elem3);
    doc.appendChild(elem1);
    let finalString = doc.documentElement.outerHTML;
    this.dataService.getDOA(finalString).subscribe(
      (result) => {
        this.xmlDoc = this.parser.parseFromString(result, 'text/xml');
        if (this.xmlDoc.getElementsByTagName('StatusCode')[0].innerHTML === 'ERROR') {
          this.doaAmounts[index] = 0;
          let errorType = 'Get DOA Error';
          let errorDescription = '';
          errorDescription = errorDescription.concat(
            'Get DOA Error Returned from Endpoint - ' +
              this.xmlDoc.getElementsByTagName('StatusCodeMsg')[0].innerHTML +
              ' - LAN ID: ' +
              lanID
          );
          let dataStructure = {
            errorType: errorType,
            errorDescription: errorDescription,
          };
          this.dialog.open(ErrorDialogComponent, {
            data: dataStructure,
            disableClose: true,
          });
        } else {
          const isProjFundDOAException = email && DOA_PROJFUND_EMAILS.includes(email.toLowerCase());
          const isREBuildingDOAException =
            email && DOA_RE_BUILDING_EMAILS.includes(email.toLowerCase());
          const standardAmount = Number(this.xmlDoc.getElementsByTagName('Amount')[0].innerHTML);

          if (isProjFundDOAException || isREBuildingDOAException) {
            const exceptionDoas = this.xmlDoc.getElementsByTagName('ExceptionDoa');
            let amount = 0;
            for (let i = 0; i < exceptionDoas.length; i++) {
              const category = exceptionDoas[i].getElementsByTagName('Category')[0];
              if (
                category &&
                category.textContent === (isProjFundDOAException ? 'PROJ-PROJFUND' : 'RE-BUILDING')
              ) {
                amount = Number(exceptionDoas[i].getElementsByTagName('Amount')[0].textContent);
                break;
              }
            }
            this.doaAmounts[index] = amount || standardAmount;
          } else {
            this.doaAmounts[index] = standardAmount;
          }
        }
      },
      (error) => {
        this.doaAmounts[index] = 0;
        let errorType = '';
        let errorDescription = '';
        if (error.error.message) {
          errorDescription = errorDescription.concat('Get DOA Error - ', error.error.message);
        } else {
          errorDescription = errorDescription.concat('Get DOA Error');
        }
        errorType = errorType.concat(error.status, ' ', error.statusText);
        let dataStructure = {
          errorType: errorType,
          errorDescription: errorDescription,
        };
        this.dialog.open(ErrorDialogComponent, {
          data: dataStructure,
          disableClose: true,
        });
      }
    );
  }
}
